import { CommonModule, Location } from '@angular/common';
import { AfterViewInit, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { blankWarrantyRecord, WarrantyStatus, IBatSku, IChoice, IMode, IWarrantyRecord, MessageType, WarrantyIMode, Company, WarrantyResponse, TOKEN_URL_PARAM_KEY, WarrantyTokens, EMPTY_WARRANTY_TOKENS, WarrantyImageMap, WarrantyImageTypes, GENERIC_CLAIM_LINK_ERROR_MESSAGE, GENERIC_SAVE_ERROR_MESSAGE } from '@models';
import { caseCompany, caseType, insensitiveEqual, insensitiveIncludes, isValidWarrantyIMode, isValidWarrantyStatus, normalizeCountry, isStateForCountry, caseCompanyIfExists } from '@utilities';
import { ApplicationService, DataService, getErrors, warrantyForm, warrantyFormToRecord } from '@warranty/services';
import { catchError, EMPTY, finalize, from, map, of, switchMap, tap } from 'rxjs';
import { WarrantyFormComponent, WarrantyImageLoaded } from './warranty-form.component';

@Component({
  selector: 'app-warranty',
  imports: [CommonModule, MatIcon, RouterModule, MatCardModule, WarrantyFormComponent],
  templateUrl: './warranty.component.html',
  styleUrl: './warranty.component.scss',
})
export class WarrantyComponent implements OnInit, AfterViewInit {
  applicationService = inject(ApplicationService);
  dataService = inject(DataService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);

  @Input() registrationId = ''; // from the url path itself after token decoded because withComponentInputBinding()
  @Input() claimId: string; // from the url path itself after token decoded because withComponentInputBinding()
  @Input(TOKEN_URL_PARAM_KEY) inputToken = ''; // from the queryParams from warranty-link.component
  private tokens: WarrantyTokens = EMPTY_WARRANTY_TOKENS;

  @Input() mode: WarrantyIMode = IMode.Registration; // from the data: { mode: ... } in app.routes

  @Input() warranty: IWarrantyRecord = { ...blankWarrantyRecord }; // result from registration.resolver

  colors = signal<string[] | undefined>(undefined);
  company = this.applicationService.company;
  companySite = this.applicationService.companySite;
  constructionType = this.applicationService.constructionType;
  filteredModels = computed(() => this.filterModels());
  levelsOfPlay = signal<IChoice[]>([]);
  models = signal<{ model: string, description: string }[] | undefined>(undefined);
  modelSelection = signal<string | undefined>(undefined);
  places = signal<IChoice[]>([]);
  processing = signal<boolean>(false);
  receipt?: File;
  serial?: File;
  damage1?: File;
  damage2?: File;
  damage3?: File;
  purchasedSite = signal<string>('');
  editable = true;
  reasons = signal<IChoice[]>([]);
  selectedColor = signal<string | undefined>(undefined);
  selectedModel = signal<string | undefined>(undefined);
  selectedSize = signal<string | undefined>(undefined);
  selectedSku = signal<IBatSku | undefined>(undefined);
  showRaw = false;
  sizes = signal<string[] | undefined>(undefined);
  useWildcard = signal<boolean>(true);
  warrantyForm = warrantyForm;
  warrantyLength = signal<IChoice[]>([]);

  Company = Company;
  IMode = IMode;
  getErrors = getErrors;
  insensitiveEqual = insensitiveEqual;
  caseCompanyIfExists = caseCompanyIfExists;

  ngAfterViewInit() {
    this.applicationService.hideSkeleton();
  }

  ngOnInit() {
    this.tokens = this.getWarrantyTokens();
    this.removeTokensFromUrl();

    if (!isValidWarrantyIMode(this.mode)) {
      this.applicationService.addMessage(`Unknown mode: ${this.mode as any}`, MessageType.ERROR);
      throw new Error('Unknown mode: ' + (this.mode as any));
    }

    if (this.mode === IMode.Claim) { this.purchasedSite.set('Other') }

    if (this.warranty.company) {
      this.warranty.company = caseCompany(this.warranty.company);
      this.applicationService.company.set(this.warranty.company);
    }
    if (this.warranty.batType) {
      this.warranty.batType = caseType(this.warranty.batType);
      this.applicationService.constructionType.set(this.warranty.batType);
    }

    this.applicationService.addClassToBody(this.applicationService.company());
    this.warrantyForm.reset();

    this.warrantyForm.patchValue({ ...blankWarrantyRecord }); // Reset the form to blankWarrantyRecord
    this.warrantyForm.patchValue(this.warranty);
    this.warrantyForm.controls.batType.setValue(this.applicationService.constructionType());

    // If we already resolved an existing registration record, skip the purchase-site gate
    // so the full form/state loads immediately.
    if (this.mode === IMode.Registration && !!this.warranty.registrationId && !!this.warranty.id) {
      this.purchasedSite.set('Other');
    }
    this.applicationService.busy.set(1);
    this.dataService.getChoices$('lookups').pipe(
      tap(results => {
        this.levelsOfPlay.set(results.levelsOfPlay ?? []);
        this.places.set(results.stores ?? []);
        this.reasons.set(results.reasons ?? []);
        this.warrantyLength.set(results.warrantyLength ?? []);
      }),
      finalize(() => {
        this.applicationService.busy.set(0);
      }),
    ).subscribe();
    this.dataService.batModels$(this.applicationService.company(), this.applicationService.constructionType()).pipe(
      tap((models) => {
        this.warrantyForm.controls.batModel.enable();
        const filteredModels: { model: string; description: string; }[] = models;
        this.models.set(filteredModels);
        this.modelSelection.set('');
        if (this.warranty.batModel) {
          this.onModelSelected(
            { target: { value: this.warranty.batModel } } as unknown as FocusEvent,
            this.warranty.batLength ?? '*',
            this.warranty.barrelColor ?? '*'
          );
        }
      }),
    ).subscribe();
    switch (this.mode) {
      case IMode.Registration:
        this.setRegistrationMode();
        break;
      case IMode.Claim:
        this.setClaimMode();
        break;
      default:
        this.applicationService.addMessage(`Unknown mode: ${this.mode as any}`, MessageType.ERROR);
        throw new Error('Unknown mode: ' + (this.mode as any));
    }
    if (this.warranty.claimId) {
      Object.values(warrantyForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
    this.updateEditableState();
  }

  onBatLengthChange(event: MatSelectChange, color?: string) {
    this.selectedSize.set(event.value);
    this.selectedColor.set(undefined);
    this.warrantyForm.controls.barrelColor.enable();
    this.colors.set(undefined);
    if (this.selectedModel() === undefined || this.selectedSize() === undefined) { return; }
    this.dataService.batModelColors$(this.applicationService.company(), this.applicationService.constructionType(), this.selectedModel() ?? '', this.selectedSize() ?? '').pipe(
      tap((colors) => {
        this.colors.set(colors);
        this.warrantyForm.controls.barrelColor.enable();
        if (colors.length <= 1) {
          const color = colors?.[0] ?? '';
          this.warrantyForm.controls.barrelColor.setValue(color);
          this.onBatColorChange({ value: color } as unknown as MatSelectChange);
          this.warrantyForm.controls.barrelColor.disable();
        }
        if (color) {
          this.warrantyForm.controls.barrelColor.setValue(color);
          this.onBatColorChange({ value: color } as unknown as MatSelectChange);
        }
      })
    ).subscribe();
  }

  onBatColorChange(event: MatSelectChange) {
    this.selectedColor.set(event.value);
    if (this.selectedModel() === undefined || this.selectedSize() === undefined || this.selectedColor === undefined) { return; }
    this.dataService.batModelSku$(this.applicationService.company(), this.applicationService.constructionType(), this.selectedModel() ?? '', this.selectedSize() ?? '', this.selectedColor() ?? '').pipe(
      tap(sku => { this.selectedSku.set(sku) }),
    ).subscribe();
  }

  onCountryChange(event: MatSelectChange) {
    const country = normalizeCountry(event.value);
    this.warrantyForm.controls.country.setValue(country);
    const state = this.warrantyForm.controls.state.value;
    if (state && !isStateForCountry(country, state)) {
      this.warrantyForm.controls.state.setValue('');
    }
    this.warrantyForm.controls.state.updateValueAndValidity();
    this.warrantyForm.controls.zipCode.updateValueAndValidity();
  }

  onImageLoaded(event: WarrantyImageLoaded) {
    this[event.imageType] = event.file;
    this.warranty[event.imageType] = event.value;
  }

  onModelChange(event: Event) {
    const result = (event.target as HTMLInputElement).value ?? undefined;
    this.useWildcard.set(event instanceof InputEvent);
    this.applicationService.clearMessages();
    this.clearModels(result);
    this.warrantyForm.controls.batLength.disable();
    this.warrantyForm.controls.barrelColor.disable();
  }

  private clearModels(result: string) {
    this.modelSelection.set(undefined);
    this.selectedColor.set(undefined);
    this.selectedSize.set(undefined);
    this.selectedSku.set(undefined);
    this.colors.set(undefined);
    this.sizes.set(undefined);
    this.modelSelection.set(result);
  }

  onModelSelection(event: MatAutocompleteSelectedEvent) {
    const result = event.option.value;
    this.useWildcard.set(event instanceof InputEvent);
    this.clearModels(result);
    this.selectedModel.set(result);
    this.onModelSelected({ target: { value: result } } as unknown as FocusEvent);
  }

  onModelSelected(event: FocusEvent, size?: string, color?: string) {
    const result = (event.target as HTMLInputElement).value;
    if (!result) return;
    this.selectedModel.set(result);
    this.sizes.set(undefined);
    this.warrantyForm.controls.batLength.enable();
    this.warrantyForm.controls.barrelColor.disable();
    this.dataService.batModelSizes$(this.applicationService.company(), this.applicationService.constructionType(), this.selectedModel() ?? '').pipe(
      map(sizes => {
        this.sizes.set(sizes);
        if (sizes.length === 1) {
          const size = sizes[0];
          this.warrantyForm.controls.batLength.setValue(size);
          this.onBatLengthChange({ value: size } as MatSelectChange);
          this.warrantyForm.controls.batLength.disable();

        }
        if (size) {
          this.warrantyForm.controls.batLength.setValue(size);
          this.onBatLengthChange({ value: size } as MatSelectChange, color);
        }
        return sizes;
      })
    ).subscribe();

  }

  onSubmit() {
    if (!this.editable) {
      return;
    }

    this.warrantyForm.markAllAsTouched();
    this.warrantyForm.updateValueAndValidity();
    if (this.warrantyForm.invalid) {
      return;
    }

    this.processing.set(true);
    this.applicationService.busy.set(1);

    this.applicationService.clearMessages();
    let result = warrantyFormToRecord(this.warrantyForm);
    if (this.mode === IMode.Registration) { result.warrantyStatus = WarrantyStatus.Registered; }
    if (this.mode === IMode.Claim) {
      if (this.canTransitionToClaimed(result.warrantyStatus)) {
        result.warrantyStatus = WarrantyStatus.Claimed;
      }
      result.claimId = result.claimId || '*';
    }
    if (isValidWarrantyIMode(this.mode)) { result.warrantyExpiration = this.calculateExpiration(result); }
    result.sku = result.sku || (this.selectedSku()?.sku ?? '');

    this.dataService.warrantyPut$(result).pipe(
      tap(status => {
        if (status.data) {
          const data: WarrantyResponse = status.data || {};
          const { warranty, tokens } = data;

          // Replace no matter what
          this.tokens = this.applicationService.replaceWarrantyTokens(tokens?.token);

          this.warrantyForm.patchValue({
            ...warranty,
            ...this.getWarrantyImageURLs()
          });
          result = warranty;
        }
      }),
      switchMap(() => {
        return this.dataService.warrantyPutImages$(result, this.getWarrantyImages());
      }),
      switchMap((statuses) => {
        console.log('Image upload statuses:', JSON.stringify(statuses, null, 2));
        const messages = Array.isArray(statuses) ? statuses.filter(status => status.message).map(status => status.message) : [statuses?.message];
        const data: IWarrantyRecord = Array.isArray(statuses) ? statuses.find(status => status.data)?.data satisfies IWarrantyRecord : statuses?.data satisfies IWarrantyRecord;
        if (!data) {
          console.error('Warranty save failed', statuses, messages);
          this.applicationService.addMessage(GENERIC_SAVE_ERROR_MESSAGE, MessageType.ERROR);
          return of(null);
        }

        const queryParams: Record<string, string> = {
          status: `${data.warrantyStatus ?? ''}`,
          id1: `${data.registrationId ?? ''}`,
          id2: `${data.claimId ?? ''}`,
        };
        if (this.tokens?.token) {
          queryParams[TOKEN_URL_PARAM_KEY] = this.tokens.token;
        }
        return from(this.router.navigate(
          ['/action-complete', data.company],
          { queryParams }
        ));
      }),
      finalize(() => {
        this.processing.set(false);
        this.applicationService.busy.set(0);

      })
    ).subscribe();
  }

  canCreateClaim(): boolean {
    return !this.editable &&
            this.mode === IMode.Registration &&
            this.warrantyForm.controls.warrantyStatus.value === WarrantyStatus.Registered &&
            !!this.tokens?.token;
  }

  createClaim() {
    if (!this.canCreateClaim()) {
      return;
    }

    const claimWindow = globalThis.open?.('', '_blank');
    if (!claimWindow) {
      this.applicationService.addMessage('Unable to open claim link. Please allow pop-ups and try again.', MessageType.ERROR);
      return;
    }

    this.dataService.warrantyClaimLink$(this.tokens.token).pipe(
      tap((status) => {
        const link = `${status?.data?.link ?? ''}`.trim();
        if (status?.error || !link) {
          console.error('Create claim link failed', status);
          throw new Error(GENERIC_CLAIM_LINK_ERROR_MESSAGE);
        }
        claimWindow.location.href = link;
      }),
      catchError((error) => {
        claimWindow.close();
        console.error('Create claim link failed', error);
        this.applicationService.addMessage(GENERIC_CLAIM_LINK_ERROR_MESSAGE, MessageType.ERROR);
        return EMPTY;
      }),
    ).subscribe();
  }

  private updateEditableState() {
    const status: string | undefined = this.warrantyForm.controls.warrantyStatus.value;
    this.editable = !isValidWarrantyStatus(status) || (this.mode === IMode.Claim && status === WarrantyStatus.Registered);
  }

  private canTransitionToClaimed(status: string | undefined): boolean {
    return !isValidWarrantyStatus(status) || status === WarrantyStatus.Registered;
  }

  /*
   * When we get into this component, either:
   * 1. We are already on a warranty id route and still have a fresh queryParam token
   *    (for example after warranty-link forwarded us here, or on a direct reload of that URL)
   *    In that case, cache the current token and use it as the source of truth
   *
   * 2. The queryParam tokens have already been removed from the current warranty id route
   *    In that case, do not overwrite the cache. Fallback to the cached token so refresh still works
   *
   * If there is no registrationId in the route or component state, this is not a valid refreshable warranty route
   *    Clear the cached warranty tokens instead of reusing them
  */
  private getWarrantyTokens(): WarrantyTokens {
    // Check if the registrationId is in the url or state
    const registrationId = `${this.activatedRoute.snapshot.params['registrationId'] || this.registrationId || ''}`.trim();

    // Are the tokens in the URL
    const hasUrlTokenParams = this.activatedRoute.snapshot.queryParams[TOKEN_URL_PARAM_KEY] !== undefined;

    const tokens: WarrantyTokens = this.applicationService.normalizeWarrantyTokens(this.inputToken);

    if (hasUrlTokenParams) {
      // If the token is in the URL, cache it
      // Typical redirection, we should expect token in the URL, so cache it, then it will be cleared from the URL next

      if (!tokens.token) {
        this.applicationService.clearWarrantyTokens();
        return EMPTY_WARRANTY_TOKENS;
      }
      return this.applicationService.replaceWarrantyTokens(tokens.token);
    }

    if (!registrationId) {
      // If there is no registrationId set anywhere, clear the tokens
      this.applicationService.clearWarrantyTokens();
      return EMPTY_WARRANTY_TOKENS;
    }

    // Typically on a refresh where the tokens have already been resolved and cached, so don't overwrite them, just use them.
    return this.applicationService.getWarrantyTokens();
  }

  private removeTokensFromUrl() {
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    if (!queryParams[TOKEN_URL_PARAM_KEY]) {
      return;
    }

    delete queryParams[TOKEN_URL_PARAM_KEY];

    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  private calculateExpiration(result: IWarrantyRecord) {
    const date = new Date(result.dateOfPurchase);
    const companyWarranty = this.warrantyLength().find(record => record.name === result.company);
    if (!companyWarranty) return date.toISOString().slice(0, 10);
    const warrantyLength = JSON.parse(companyWarranty?.description)[this.applicationService.constructionType()] ?? 0;
    date.setDate(date.getDate() + warrantyLength);
    return date.toISOString().slice(0, 10);
  }

  private filterModels() {
    const selected = this.modelSelection();
    if (!selected) return this.models();
    const words = selected.split(' ');
    const result = this.models()?.filter(model => this.useWildcard() ?
      words.every(word => insensitiveIncludes(model.description, word)) || words.every(word => insensitiveIncludes(model.model, word)) :
      insensitiveEqual(model.description, selected) || insensitiveEqual(model.model, selected)
    );
    return result;
  }

  private setRegistrationMode(): void {
    this.warrantyForm.controls.line1.disable();
    this.warrantyForm.controls.city.disable();
    this.warrantyForm.controls.state.disable();
    this.warrantyForm.controls.zipCode.disable();
    this.warrantyForm.controls.reason.disable();
    this.warrantyForm.controls.phone.enable();
    this.warrantyForm.controls.batModel.disable();
    this.warrantyForm.controls.batLength.disable();
    this.warrantyForm.controls.barrelColor.disable();
  }

  private setClaimMode(): void {
    this.warrantyForm.controls.line1.enable();
    this.warrantyForm.controls.city.enable();
    this.warrantyForm.controls.state.enable();
    this.warrantyForm.controls.zipCode.enable();
    this.warrantyForm.controls.reason.enable();
    this.warrantyForm.controls.phone.enable();
    this.warrantyForm.controls.batModel.enable();
    this.warrantyForm.controls.batLength.enable();
    this.warrantyForm.controls.barrelColor.enable();
  }

  private getWarrantyImages(): WarrantyImageMap<File> {
    const images: WarrantyImageMap<File> = {};
    WarrantyImageTypes.forEach(imageType => {
      const image: File = this[imageType];
      if (!image) return;

      images[imageType] = image;
    });
    return images;
  }

  private getWarrantyImageURLs(): WarrantyImageMap<string> {
    const imageURLs: WarrantyImageMap<string> = {};
    WarrantyImageTypes.forEach(imageType => {
      const image: File = this[imageType];
      const url: string = this.warrantyForm.controls[imageType]?.value;
      if (!image || !url) return;

      imageURLs[imageType] = url;
    });
    return imageURLs;
  }
}
